import { datadogRum } from '@datadog/browser-rum';
import { Button, ButtonVariants, FormControlSizes, IconName, NoteVariant, useNotes } from '@talos/kyoko';
import { createContext, useEffect, useRef, type PropsWithChildren } from 'react';
import { ReplaySubject } from 'rxjs';
import { OrgConfigurationKey, useOrgConfiguration } from './OrgConfigurationProvider';

export const successSubject = new ReplaySubject<ServiceWorkerRegistration>(undefined);
export const updateSubject = new ReplaySubject<string>(undefined);

export const ServiceWorkerContext = createContext(null);
ServiceWorkerContext.displayName = 'ServiceWorkerContext';

const activateNewServiceWorker = () => {
  window.location.reload();
};

export const ServiceWorkerProvider = function ServiceWorkerProvider({ children }: PropsWithChildren) {
  const { addNote } = useNotes();
  const { getConfig } = useOrgConfiguration();
  const isShowingNotification = useRef(false);

  // Subscribe to service worker updates
  useEffect(() => {
    const sub = updateSubject.subscribe(() => {
      if (!isShowingNotification.current) {
        addNote({
          id: import.meta.env.VITE_GIT_HASH,
          closable: true,
          icon: IconName.Speakerphone,
          variant: NoteVariant.Announcement,
          text: (
            <>
              <strong>There&apos;s a new version available! </strong>
              Click the button to the right or refresh your page to update.
            </>
          ),
          actions: (
            <>
              <Button
                size={FormControlSizes.Small}
                variant={ButtonVariants.Positive}
                onClick={activateNewServiceWorker}
              >
                Update
              </Button>
            </>
          ),
        });
        isShowingNotification.current = true;
      }
    });
    return () => {
      sub.unsubscribe();
    };
  }, [addNote]);

  // Arbitrarily put this here in this component, but could live anywhere...
  const didForceSessionReplay = useRef(false);
  useEffect(() => {
    const shouldForceSessionReplay =
      getConfig(OrgConfigurationKey.ForceSessionReplay, false) && !didForceSessionReplay.current;
    if (shouldForceSessionReplay) {
      didForceSessionReplay.current = true;
      datadogRum.startSessionReplayRecording({ force: true });
    }
  }, [getConfig]);

  return <>{children}</>;
};
